/**
 * InputFieldsEditor Component
 * 
 * Editor for input_fields array within a transition_change.
 * Used when change_type is "user_input" to define what input fields
 * should be presented to users when executing a transition.
 */

import React from 'react';
import {
  Form,
  Input,
  Select,
  Switch,
  Button,
  Space,
  InputNumber,
  Row,
  Col,
  Divider,
  Typography,
  Card
} from 'antd';
import { PlusOutlined, DeleteOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { InputFieldDefinition, PropertyType } from '../../types/globalProperties';
import { INPUT_MASK_TYPES } from '../../constants/globalPropertyConstants';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const PROPERTY_TYPES: PropertyType[] = [
  'string',
  'number',
  'integer',
  'float',
  'boolean',
  'date',
  'date_range',
  'currency',
  'duration',
  'url',
  'uuid',
  'uuid[]',
  'list'
];

// Helper to check if a property type is numeric
const isNumericPropertyType = (type: string): boolean => {
  return ['number', 'integer', 'float', 'currency', 'duration'].includes(type);
};

interface InputFieldsEditorProps {
  inputFields: InputFieldDefinition[];
  onChange: (fields: InputFieldDefinition[]) => void;
  disabled?: boolean;
  propertyKey?: string; // The property_key that these fields are for (used as default name)
}

export const InputFieldsEditor: React.FC<InputFieldsEditorProps> = ({
  inputFields = [],
  onChange,
  disabled = false,
  propertyKey
}) => {
  const handleAddField = () => {
    const newField: InputFieldDefinition = {
      name: propertyKey || '',
      type: 'string',
      label: '',
      required: false
    };
    onChange([...inputFields, newField]);
  };

  const handleRemoveField = (index: number) => {
    const updated = inputFields.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleFieldChange = (index: number, field: Partial<InputFieldDefinition>) => {
    const updated = [...inputFields];
    updated[index] = {
      ...updated[index],
      ...field
    };
    onChange(updated);
  };

  return (
    <div>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {inputFields.map((field, index) => (
          <Card
            key={index}
            size="small"
            title={`Input Field ${index + 1}`}
            extra={
              !disabled && (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemoveField(index)}
                  size="small"
                >
                  Remove
                </Button>
              )
            }
            style={{ marginBottom: 16 }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <div>
                    <Text strong>Name</Text>
                    <Input
                      value={field.name}
                      onChange={(e) => handleFieldChange(index, { name: e.target.value })}
                      placeholder={propertyKey || 'e.g., property_key'}
                      disabled={disabled}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Maps to property_key or custom field name
                    </Text>
                  </div>

                  <div>
                    <Text strong>Type</Text>
                    <Select
                      value={field.type}
                      onChange={(value) => handleFieldChange(index, { type: value })}
                      style={{ width: '100%' }}
                      disabled={disabled}
                    >
                      {PROPERTY_TYPES.map((type) => (
                        <Option key={type} value={type}>
                          {type}
                        </Option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <Text strong>Label</Text>
                    <Input
                      value={field.label}
                      onChange={(e) => handleFieldChange(index, { label: e.target.value })}
                      placeholder="Display label for the input field"
                      disabled={disabled}
                    />
                  </div>

                  <div>
                    <Space>
                      <Switch
                        checked={field.required}
                        onChange={(checked) => handleFieldChange(index, { required: checked })}
                        disabled={disabled}
                      />
                      <Text>Required</Text>
                    </Space>
                  </div>
                </Space>
              </Col>

              <Col span={12}>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <div>
                    <Text strong>Placeholder</Text>
                    <Input
                      value={field.placeholder}
                      onChange={(e) => handleFieldChange(index, { placeholder: e.target.value })}
                      placeholder="Placeholder text"
                      disabled={disabled}
                    />
                  </div>

                  <div>
                    <Text strong>Help Text</Text>
                    <TextArea
                      value={field.help_text}
                      onChange={(e) => handleFieldChange(index, { help_text: e.target.value })}
                      placeholder="Additional guidance for users"
                      rows={2}
                      disabled={disabled}
                    />
                  </div>

                  <div>
                    <Text strong>Default Value</Text>
                    <Input
                      value={field.default_value !== undefined ? String(field.default_value) : ''}
                      onChange={(e) => handleFieldChange(index, { default_value: e.target.value })}
                      placeholder="Default value"
                      disabled={disabled}
                    />
                  </div>

                  {field.type === 'string' && (
                    <>
                      <div>
                        <Text strong>Input Mask</Text>
                        <Select
                          value={field.input_mask}
                          onChange={(value) => handleFieldChange(index, { 
                            input_mask: value,
                            ...(value !== 'custom' ? { input_mask_pattern: undefined } : {})
                          })}
                          placeholder="Select input mask (optional)"
                          allowClear
                          style={{ width: '100%' }}
                          disabled={disabled}
                        >
                          {INPUT_MASK_TYPES.map(mask => (
                            <Option key={mask.value} value={mask.value}>
                              {mask.label}
                            </Option>
                          ))}
                        </Select>
                      </div>
                      {field.input_mask === 'custom' && (
                        <div>
                          <Text strong>Custom Mask Pattern</Text>
                          <Input
                            value={field.input_mask_pattern || ''}
                            onChange={(e) => handleFieldChange(index, { input_mask_pattern: e.target.value })}
                            placeholder="e.g., ^[A-Z]{2}\\d{4}$"
                            disabled={disabled}
                          />
                        </div>
                      )}
                    </>
                  )}
                </Space>
              </Col>
            </Row>

            {/* Validation Rules */}
            <Divider style={{ margin: '12px 0' }} />
            <Text strong>Validation Rules</Text>
            <Row gutter={16} style={{ marginTop: 8 }}>
              <Col span={12}>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {isNumericPropertyType(field.type) && (
                    <>
                      <div>
                        <Text>Min</Text>
                        <InputNumber
                          value={field.validation_rules?.min}
                          onChange={(value) =>
                            handleFieldChange(index, {
                              validation_rules: {
                                ...field.validation_rules,
                                min: value ?? undefined
                              }
                            })
                          }
                          style={{ width: '100%' }}
                          disabled={disabled}
                        />
                      </div>
                      <div>
                        <Text>Max</Text>
                        <InputNumber
                          value={field.validation_rules?.max}
                          onChange={(value) =>
                            handleFieldChange(index, {
                              validation_rules: {
                                ...field.validation_rules,
                                max: value ?? undefined
                              }
                            })
                          }
                          style={{ width: '100%' }}
                          disabled={disabled}
                        />
                      </div>
                    </>
                  )}
                  {field.type === 'string' && (
                    <>
                      <div>
                        <Text>Min Length</Text>
                        <InputNumber
                          value={field.validation_rules?.min_length}
                          onChange={(value) =>
                            handleFieldChange(index, {
                              validation_rules: {
                                ...field.validation_rules,
                                min_length: value ?? undefined
                              }
                            })
                          }
                          style={{ width: '100%' }}
                          min={0}
                          disabled={disabled}
                        />
                      </div>
                      <div>
                        <Text>Max Length</Text>
                        <InputNumber
                          value={field.validation_rules?.max_length}
                          onChange={(value) =>
                            handleFieldChange(index, {
                              validation_rules: {
                                ...field.validation_rules,
                                max_length: value ?? undefined
                              }
                            })
                          }
                          style={{ width: '100%' }}
                          min={0}
                          disabled={disabled}
                        />
                      </div>
                      <div>
                        <Text>Pattern (Regex)</Text>
                        <Input
                          value={field.validation_rules?.pattern}
                          onChange={(e) =>
                            handleFieldChange(index, {
                              validation_rules: {
                                ...field.validation_rules,
                                pattern: e.target.value || undefined
                              }
                            })
                          }
                          placeholder="Regular expression pattern"
                          disabled={disabled}
                        />
                      </div>
                    </>
                  )}
                </Space>
              </Col>
            </Row>

            {/* Options for select/enum types */}
            {(field.type === 'list' || field.type === 'string') && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <Text strong>Options (for select/enum)</Text>
                <div style={{ marginTop: 8 }}>
                  {field.options?.map((option, optIndex) => (
                    <Space key={optIndex} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Input
                        placeholder="Value"
                        value={option.value}
                        onChange={(e) => {
                          const updatedOptions = [...(field.options || [])];
                          updatedOptions[optIndex] = { ...option, value: e.target.value };
                          handleFieldChange(index, { options: updatedOptions });
                        }}
                        disabled={disabled}
                        style={{ width: 150 }}
                      />
                      <Input
                        placeholder="Label"
                        value={option.label}
                        onChange={(e) => {
                          const updatedOptions = [...(field.options || [])];
                          updatedOptions[optIndex] = { ...option, label: e.target.value };
                          handleFieldChange(index, { options: updatedOptions });
                        }}
                        disabled={disabled}
                        style={{ width: 150 }}
                      />
                      {!disabled && (
                        <Button
                          type="text"
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => {
                            const updatedOptions = field.options?.filter((_, i) => i !== optIndex) || [];
                            handleFieldChange(index, { options: updatedOptions.length > 0 ? updatedOptions : undefined });
                          }}
                        />
                      )}
                    </Space>
                  ))}
                  {!disabled && (
                    <Button
                      type="dashed"
                      onClick={() => {
                        const updatedOptions = [...(field.options || []), { value: '', label: '' }];
                        handleFieldChange(index, { options: updatedOptions });
                      }}
                      icon={<PlusOutlined />}
                      block
                      disabled={disabled}
                    >
                      Add Option
                    </Button>
                  )}
                </div>
              </>
            )}
          </Card>
        ))}

        {!disabled && (
          <Button
            type="dashed"
            onClick={handleAddField}
            icon={<PlusOutlined />}
            block
          >
            Add Input Field
          </Button>
        )}

        {inputFields.length === 0 && (
          <Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
            No input fields defined. Click "Add Input Field" to create one.
          </Text>
        )}
      </Space>
    </div>
  );
};

